import { rp, thumb } from "@/lib/format";

/**
 * Share card renderer — PURE Canvas 2D, zero html2canvas.
 * Semua digambar piksel-demi-piksel dengan koordinat eksplisit:
 * tidak ada CSS, tidak ada font-layout engine browser, tidak ada bounding box tabrakan.
 *
 * Output: PNG blob 1080x1350.
 */

const W = 1080;
const H = 1350;
const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "'Plus Jakarta Sans', Arial, sans-serif";

// ── Base theme zunayya (dari tailwind.config) ──
const THEME = {
  plum: "#3B2E4C",
  plumDeep: "#25172e",
  sakura: "#F5A9C7",
  sakuraDeep: "#e57ba8",
  butter: "#FFDEB0",
  butterSoft: "#FFE8A6",
  lilac: "#C8B4E4",
  mint: "#0e9f6e",
  cream: "#FFF6EC",
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function drawCover(ctx, img, x, y, w, h) {
  const scale = Math.max(w / img.width, h / img.height);
  const sw = w / scale;
  const sh = h / scale;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function wrapText(ctx, text, maxWidth, maxLines) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = line ? line + " " + word : word;
    if (ctx.measureText(test).width <= maxWidth) {
      line = test;
    } else {
      if (line) lines.push(line);
      line = word;
      if (lines.length === maxLines - 1) break;
    }
  }
  if (line) lines.push(line);
  // Ellipsis kalau ada sisa kata yang tidak muat
  const used = lines.join(" ").length;
  if (used < text.trim().length && lines.length) {
    let last = lines[lines.length - 1];
    while (last.length > 0 && ctx.measureText(last + "…").width > maxWidth) {
      last = last.slice(0, -1);
    }
    lines[lines.length - 1] = last.trimEnd() + "…";
  }
  return lines;
}

/**
 * @param {object} product
 * @param {{brandName?: string, tripName?: string, logoUrl?: string}} opts
 * @returns {Promise<Blob|null>}
 */
export async function renderShareCardCanvas(product, opts = {}) {
  const brandName = opts.brandName || "Zunayya";
  const tripName = opts.tripName || "";
  const logoUrl = opts.logoUrl || "/logo.png";

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  const discount = product?.diskon_persen || 0;
  const showCoret = product?.harga_coret_idr && product.harga_coret_idr > product?.harga_idr;
  const hemat = showCoret ? product.harga_coret_idr - product.harga_idr : 0;
  const soldOut = product?.stock_status === "sold_out";
  const photoSrc = product?.foto_urls?.[0] ? thumb(product.foto_urls[0], 1200) : null;

  // ── 1. Background: foto cover, atau gradient branded kalau tanpa foto ──
  const photo = photoSrc ? await loadImage(photoSrc) : null;
  if (photo) {
    drawCover(ctx, photo, 0, 0, W, H);
  } else {
    // Branded fallback: gradient sakura → lilac → plum (base theme)
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, THEME.sakura);
    g.addColorStop(0.5, THEME.lilac);
    g.addColorStop(1, THEME.plum);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }

  // ── 2. Gradient bawah (teks zone) — plum theme, bukan hitam generik ──
  const grad = ctx.createLinearGradient(0, H - 680, 0, H);
  grad.addColorStop(0, "rgba(59,46,76,0)");
  grad.addColorStop(0.35, "rgba(59,46,76,0.38)");
  grad.addColorStop(0.65, "rgba(47,33,60,0.9)");
  grad.addColorStop(1, "rgba(37,23,46,0.97)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, H - 680, W, 680);

  // ── 3. Badge diskon (kiri atas) ──
  ctx.textBaseline = "middle";
  if (discount > 0 && !soldOut) {
    const label = `−${discount}% OFF`;
    ctx.font = `800 36px ${SANS}`;
    const tw = ctx.measureText(label).width;
    const bw = tw + 68;
    const bh = 72;
    const bx = 44;
    const by = 44;
    const bg = ctx.createLinearGradient(bx, by, bx + bw, by);
    bg.addColorStop(0, "#ff2d55");
    bg.addColorStop(0.6, "#ff6a00");
    bg.addColorStop(1, "#ff9d2e");
    roundRect(ctx, bx, by, bw, bh, bh / 2);
    ctx.fillStyle = bg;
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "left";
    ctx.fillText(label, bx + 34, by + bh / 2 + 2);
  }

  // ── 4. SOLD OUT overlay ──
  if (soldOut) {
    ctx.fillStyle = "rgba(37,23,46,0.62)";
    ctx.fillRect(0, 0, W, H);
    ctx.font = `800 46px ${SANS}`;
    const label = "H A B I S";
    const tw = ctx.measureText(label).width;
    const bw = tw + 120;
    const bh = 96;
    const bx = (W - bw) / 2;
    const by = H / 2 - bh / 2;
    roundRect(ctx, bx, by, bw, bh, bh / 2);
    ctx.fillStyle = "rgba(255,246,236,0.95)";
    ctx.fill();
    ctx.fillStyle = THEME.plum;
    ctx.textAlign = "center";
    ctx.fillText(label, W / 2, by + bh / 2 + 2);
  }

  // ── 5. Konten bawah (center stack, y dihitung dari bawah) ──
  const cx = W / 2;
  let cursorY = H - 60; // baseline bawah, naik bertahap

  // Footer — sakura soft, match theme
  ctx.textAlign = "center";
  ctx.font = `600 17px ${SANS}`;
  ctx.fillStyle = "rgba(245,169,199,0.75)";
  const footer = "O R D E R   V I A   W H A T S A P P";
  ctx.fillText(footer, cx, cursorY);
  cursorY -= 56;

  // Harga row: ukur total width, center sebagai grup
  const priceText = rp(product?.harga_idr || 0);
  ctx.font = `italic 80px ${SERIF}`;
  const priceW = ctx.measureText(priceText).width;
  let coretText = null;
  let coretW = 0;
  if (showCoret) {
    coretText = rp(product.harga_coret_idr);
    ctx.font = `500 32px ${SANS}`;
    coretW = ctx.measureText(coretText).width + 6; // + padding garis
  }
  let hematW = 0;
  let hematText = null;
  if (hemat > 0) {
    hematText = `HEMAT ${rp(hemat)}`;
    ctx.font = `800 26px ${SANS}`;
    hematW = ctx.measureText(hematText).width + 52;
  }
  const gap = 20;
  const totalW = priceW + (coretW ? coretW + gap : 0) + (hematW ? hematW + gap : 0);
  let x = cx - totalW / 2;

  ctx.textAlign = "left";
  ctx.font = `italic 80px ${SERIF}`;
  ctx.fillStyle = THEME.butterSoft;
  ctx.shadowColor = "rgba(0,0,0,0.55)";
  ctx.shadowBlur = 16;
  ctx.shadowOffsetY = 3;
  ctx.fillText(priceText, x, cursorY);
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  x += priceW + gap;

  if (coretText) {
    ctx.font = `500 32px ${SANS}`;
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.fillText(coretText, x, cursorY + 8);
    // Garis strike manual — posisi matematis, selalu pas
    const cw = ctx.measureText(coretText).width;
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.fillRect(x, cursorY + 6, cw, 3);
    x += coretW + gap;
  }

  if (hematText) {
    ctx.font = `800 26px ${SANS}`;
    const bw = hematW;
    const bh = 52;
    const bx = x;
    const by = cursorY - bh / 2 + 6;
    roundRect(ctx, bx, by, bw, bh, bh / 2);
    ctx.fillStyle = THEME.mint;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "rgba(255,255,255,0.4)";
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.fillText(hematText, bx + 26, by + bh / 2 + 1);
  }
  cursorY -= 120;

  // Title (max 2 baris, wrap by measureText)
  ctx.font = `italic 64px ${SERIF}`;
  ctx.fillStyle = "#ffffff";
  ctx.shadowColor = "rgba(0,0,0,0.6)";
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 3;
  ctx.textAlign = "center";
  const titleLines = wrapText(ctx, (product?.nama || "Produk").trim(), W - 160, 2);
  const titleLH = 80;
  const titleStartY = cursorY - (titleLines.length - 1) * titleLH;
  titleLines.forEach((ln, i) => {
    ctx.fillText(ln, cx, titleStartY + i * titleLH);
  });
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  cursorY = titleStartY - 64;

  // Divider buttercream gold
  ctx.fillStyle = "rgba(255,222,176,0.85)";
  roundRect(ctx, cx - 36, cursorY, 72, 3, 2);
  ctx.fill();
  cursorY -= 44;

  // Brand row: logo + teks (center sebagai grup)
  ctx.font = `italic 36px ${SERIF}`;
  const brandW = ctx.measureText(brandName).width;
  const logoSize = 68;
  const logoGap = 18;
  const groupW = logoSize + logoGap + brandW;
  const logoX = cx - groupW / 2;
  const logoY = cursorY - logoSize + 12;

  const logo = await loadImage(logoUrl);
  if (logo) {
    ctx.save();
    roundRect(ctx, logoX, logoY, logoSize, logoSize, 20);
    ctx.clip();
    drawCover(ctx, logo, logoX, logoY, logoSize, logoSize);
    ctx.restore();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "rgba(255,232,166,0.6)";
    roundRect(ctx, logoX, logoY, logoSize, logoSize, 20);
    ctx.stroke();
  }
  ctx.textAlign = "left";
  ctx.font = `italic 36px ${SERIF}`;
  ctx.fillStyle = "#ffffff";
  ctx.fillText(brandName, logoX + logoSize + logoGap, cursorY - 22);
  ctx.font = `600 16px ${SANS}`;
  ctx.fillStyle = "rgba(255,222,176,0.85)";
  ctx.fillText(`JASTIP THAILAND${tripName ? ` · ${tripName.toUpperCase()}` : ""}`, logoX + logoSize + logoGap, cursorY + 12);

  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), "image/png");
    } catch {
      resolve(null); // canvas tainted (CORS foto) — caller fallback
    }
  });
}
